import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './ProfileOnboardingModal.css';

const MENTEE_GOALS = [
  'Get a software job',
  'Switch careers into tech',
  'Improve coding skills',
  'Crack interviews (FAANG)',
  'Learn a specific technology',
  'Build a side project',
  'Get promoted',
  'Start freelancing',
];

const MENTOR_SKILLS = [
  'JavaScript', 'Python', 'React', 'Node.js', 'Java', 'Go',
  'System Design', 'DSA & Algorithms', 'Machine Learning', 'DevOps',
  'Cloud (AWS/GCP/Azure)', 'Mobile (iOS/Android)', 'Data Science',
  'Product Management', 'Career Coaching', 'Interview Prep',
];

const COUNTRY_CODES = [
  { code: '+91',  flag: '🇮🇳' },
  { code: '+1',   flag: '🇺🇸' },
  { code: '+44',  flag: '🇬🇧' },
  { code: '+61',  flag: '🇦🇺' },
  { code: '+65',  flag: '🇸🇬' },
  { code: '+971', flag: '🇦🇪' },
  { code: '+60',  flag: '🇲🇾' },
];

export function isOnboardingNeeded(userId) {
  try {
    const raw = localStorage.getItem(`db_onboarded_${userId}`);
    if (!raw) return true;
    const { skippedAt, completedAt } = JSON.parse(raw);
    if (completedAt) return false;
    if (skippedAt) return Date.now() - skippedAt > 7 * 24 * 60 * 60 * 1000;
    return true;
  } catch { return true; }
}

function markDone(userId) {
  try { localStorage.setItem(`db_onboarded_${userId}`, JSON.stringify({ completedAt: Date.now() })); } catch {}
}
function markSkipped(userId) {
  try { localStorage.setItem(`db_onboarded_${userId}`, JSON.stringify({ skippedAt: Date.now() })); } catch {}
}

export default function ProfileOnboardingModal({ open, onClose }) {
  const { user, updateUser } = useAuth();

  const [step,          setStep]         = useState(1);
  const [countryCode,   setCountryCode]  = useState('+91');
  const [phone,         setPhone]        = useState('');
  const [location,      setLocation]     = useState(user?.location || '');
  const [headline,      setHeadline]     = useState(user?.headline || '');
  const [goals,         setGoals]        = useState([]);
  const [skills,        setSkills]       = useState([]);
  const [customSkill,   setCustomSkill]  = useState('');
  const [linkedinUrl,   setLinkedinUrl]  = useState(user?.linkedinUrl || '');
  const [githubUrl,     setGithubUrl]    = useState(user?.githubUrl || '');
  const [saving,        setSaving]       = useState(false);

  if (!open || !user) return null;

  const isMentor = user.role === 'mentor';
  const STEPS = 2;

  const toggleGoal  = (g) => setGoals(p => p.includes(g)  ? p.filter(x => x !== g)  : p.length < 5 ? [...p, g]  : p);
  const toggleSkill = (s) => setSkills(p => p.includes(s) ? p.filter(x => x !== s) : p.length < 8 ? [...p, s] : p);

  const addCustomSkill = () => {
    const s = customSkill.trim();
    if (s && !skills.includes(s) && skills.length < 8) {
      setSkills(p => [...p, s]);
      setCustomSkill('');
    }
  };

  const buildPatch = () => ({
    phone:       phone ? countryCode + phone : undefined,
    location:    location  || undefined,
    headline:    headline  || undefined,
    linkedinUrl: linkedinUrl || undefined,
    githubUrl:   githubUrl  || undefined,
    ...(isMentor ? { skills } : { goals }),
    profileComplete: Math.min(100, (user.profileComplete || 20) + 45),
  });

  const handleSave = async () => {
    setSaving(true);
    await updateUser(buildPatch());
    markDone(user.id);
    setSaving(false);
    onClose();
  };

  const handleSkipStep2 = async () => {
    // Save step-1 data even when skipping step 2
    if (phone || location || headline) {
      await updateUser({
        phone:    phone ? countryCode + phone : undefined,
        location: location || undefined,
        headline: headline || undefined,
        profileComplete: Math.min(100, (user.profileComplete || 20) + 20),
      });
    }
    markDone(user.id);
    onClose();
  };

  const handleSkipAll = () => {
    markSkipped(user.id);
    onClose();
  };

  const STEP_LABELS = ['Contact & Location', isMentor ? 'Skills & Links' : 'Goals & Links'];

  const BENEFITS = isMentor
    ? ['⚡ Higher visibility', '🎯 Matched to right mentees', '💼 More bookings']
    : ['🎯 Personalised mentor picks', '⚡ Faster session setup', '🎓 Track your progress'];

  return (
    <div className="pob-overlay">
      <div className="pob-box">

        {/* ── Progress bar ── */}
        <div className="pob-progress-bar">
          <div className="pob-progress-fill" style={{ width: `${(step / STEPS) * 100}%` }} />
        </div>
        <div className="pob-step-label">Step {step} of {STEPS} — {STEP_LABELS[step - 1]}</div>

        {/* ── Banner ── */}
        <div className="pob-banner">
          <div className="pob-banner-avatar" style={{ background: `#${user.avatarBg || '1DB954'}` }}>
            {user.initials}
          </div>
          <div className="pob-banner-text">
            {step === 1 ? (
              <>
                <h2>You're in, {user.firstName}! 🎉</h2>
                <p>Complete your profile to unlock the best of DevBrains.</p>
              </>
            ) : (
              <>
                <h2>{isMentor ? 'Showcase your expertise' : 'Set your learning goals'}</h2>
                <p>{isMentor ? 'Help mentees find exactly the right mentor.' : 'We\'ll match you with mentors who\'ve been there.'}</p>
              </>
            )}
            <div className="pob-benefits">
              {BENEFITS.map(b => <span key={b} className="pob-benefit-chip">{b}</span>)}
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="pob-body">

          {step === 1 && (
            <>
              {/* Phone */}
              <div className="pob-field">
                <label className="pob-label">📱 Phone Number</label>
                <div className="pob-phone-row">
                  <select
                    className="pob-cc-select"
                    value={countryCode}
                    onChange={e => setCountryCode(e.target.value)}
                  >
                    {COUNTRY_CODES.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input
                    className="pob-input pob-input-phone"
                    type="tel"
                    placeholder="9876543210"
                    value={phone}
                    onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 12))}
                  />
                </div>
                <span className="pob-hint">Only used for session reminders. Never shown publicly.</span>
              </div>

              {/* Location */}
              <div className="pob-field">
                <label className="pob-label">📍 Location</label>
                <input
                  className="pob-input"
                  type="text"
                  placeholder="Mumbai, India"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                />
              </div>

              {/* Headline */}
              <div className="pob-field">
                <label className="pob-label">
                  {isMentor ? '💼 Professional Headline' : '🎓 Current Role or Goal'}
                </label>
                <input
                  className="pob-input"
                  type="text"
                  placeholder={isMentor ? 'Senior Engineer @ Google · 8 yrs exp' : 'Aspiring Full-Stack Developer'}
                  value={headline}
                  maxLength={80}
                  onChange={e => setHeadline(e.target.value)}
                />
                <span className="pob-hint pob-hint-right">{headline.length}/80</span>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {/* Role-specific section */}
              {isMentor ? (
                <div className="pob-field">
                  <label className="pob-label">
                    🏷️ Skills You Mentor In
                    <span className="pob-count-badge">{skills.length}/8</span>
                  </label>
                  <div className="pob-tags">
                    {MENTOR_SKILLS.map(s => (
                      <button
                        key={s}
                        type="button"
                        className={`pob-tag${skills.includes(s) ? ' pob-tag--on' : ''}`}
                        onClick={() => toggleSkill(s)}
                      >{s}</button>
                    ))}
                  </div>
                  <div className="pob-custom-row">
                    <input
                      className="pob-input"
                      placeholder="Add another skill…"
                      value={customSkill}
                      onChange={e => setCustomSkill(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addCustomSkill()}
                    />
                    <button type="button" className="pob-add-btn" onClick={addCustomSkill}>Add</button>
                  </div>
                  {skills.filter(s => !MENTOR_SKILLS.includes(s)).length > 0 && (
                    <div className="pob-custom-tags">
                      {skills.filter(s => !MENTOR_SKILLS.includes(s)).map(s => (
                        <span key={s} className="pob-custom-chip">
                          {s}
                          <button type="button" onClick={() => toggleSkill(s)}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="pob-field">
                  <label className="pob-label">
                    🎯 What are your learning goals?
                    <span className="pob-count-badge">{goals.length}/5</span>
                  </label>
                  <div className="pob-tags">
                    {MENTEE_GOALS.map(g => (
                      <button
                        key={g}
                        type="button"
                        className={`pob-tag${goals.includes(g) ? ' pob-tag--on' : ''}`}
                        onClick={() => toggleGoal(g)}
                      >{g}</button>
                    ))}
                  </div>
                </div>
              )}

              {/* Social links */}
              <div className="pob-field">
                <label className="pob-label">🔗 LinkedIn Profile <span className="pob-optional">optional</span></label>
                <input
                  className="pob-input"
                  type="url"
                  placeholder="https://linkedin.com/in/yourname"
                  value={linkedinUrl}
                  onChange={e => setLinkedinUrl(e.target.value)}
                />
              </div>
              <div className="pob-field">
                <label className="pob-label">💻 GitHub Profile <span className="pob-optional">optional</span></label>
                <input
                  className="pob-input"
                  type="url"
                  placeholder="https://github.com/yourname"
                  value={githubUrl}
                  onChange={e => setGithubUrl(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="pob-footer">
          <div className="pob-footer-main">
            {step > 1 && (
              <button type="button" className="pob-back-btn" onClick={() => setStep(s => s - 1)}>
                ← Back
              </button>
            )}
            {step < STEPS ? (
              <button type="button" className="pob-next-btn" onClick={() => setStep(s => s + 1)}>
                Continue →
              </button>
            ) : (
              <button type="button" className="pob-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? '⏳ Saving…' : '✅ Save & Get Started'}
              </button>
            )}
          </div>
          <button
            type="button"
            className="pob-skip-btn"
            onClick={step === STEPS ? handleSkipStep2 : handleSkipAll}
          >
            {step === STEPS ? 'Skip this step' : 'Skip for now'}
          </button>
        </div>

      </div>
    </div>
  );
}
